import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core'

/**
 * Makes a screen preview inert without killing hover.
 *
 * Reviewers need PrimeNG tooltips and hover states to work so they can inspect
 * disabled-button reasons, which rules out `pointer-events: none`. Instead this
 * blocks click, mousedown (focus shifts) and Enter/Space in the capture phase,
 * which runs before any child handler — so chip clicks, resource-table row
 * clicks, filter pills and expand chevrons are all short-circuited.
 *
 * Capture-phase listeners outlive Angular's view destruction, hence the explicit
 * detach in ngOnDestroy.
 */
@Directive({
  selector: '[showcaseReadOnlyPreview]',
  standalone: false,
})
export class ReadOnlyPreviewDirective implements OnInit, OnDestroy {
  constructor(private readonly host: ElementRef<HTMLElement>) {}

  private readonly blockPointer = (event: Event): void => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private readonly blockKey = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  ngOnInit(): void {
    const el = this.host.nativeElement
    el.addEventListener('click', this.blockPointer, true)
    el.addEventListener('mousedown', this.blockPointer, true)
    el.addEventListener('keydown', this.blockKey, true)
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement
    el.removeEventListener('click', this.blockPointer, true)
    el.removeEventListener('mousedown', this.blockPointer, true)
    el.removeEventListener('keydown', this.blockKey, true)
  }
}
